import { Button, Tooltip } from '@mui/material';
import type {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
} from 'ag-grid-community';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import AgGrid from 'src/components/AgGrid';
import {
  ArrowRightIcon,
  ForwardIcon,
  InformationCircleIcon,
} from 'src/components/icons';
import { StatusCellRenderer } from 'src/components/StatusCellRenderer';
import { Client, CONTRACT_DETAILS_PAGE, CONTRACTS_PAGE } from 'src/constants';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { Contract } from 'src/entities/Contract';
import history from 'src/history';
import { useModuleIcon } from 'src/hooks/useModuleIcon';
import { useAppSelector } from 'src/hooks/useStore';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import { typography13RegularStyle } from 'src/legacy/components/Text';
import { CreateButton } from 'src/legacy/components/UI';
import StandardPage from 'src/legacy/components/UI/StandardPage';
import {
  ContractStatus,
  ContractTemplate,
  useGetContractsQuery,
  useGetContractTemplatesQuery,
} from 'src/services/api';
import { selectAllCompaniesMap } from 'src/store/clients/types';
import { black, white } from 'src/theme/colors';
import { arrayToMap } from 'src/utils/array';
import DateUtils from 'src/utils/DateUtils';

import MemoBaseTypography from '../Text/BaseTypography';
import { TABS_CONTAINER_HEIGHT } from '../UI/BaseTab';
import { TableWrapper } from '../UI/Tables/TableWrapper';

import { getContractStatusInfo } from './ClientContractsTable';
import { ContractClientRenderer } from './ContractClientRenderer';
import { ContractDetailsPageView } from './ContractDetailsPage';
import { SubmissionTableActionMenu } from './SubmissionAndRequestTable';

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    [theme.breakpoints.down('sm')]: { padding: 0, marginTop: 0 },
  },
  contractsRoot: {
    marginTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(4.5),
    paddingRight: theme.spacing(4.5),
  },
  button: {
    justifyContent: 'flex-start',
    '&:hover': {
      background: 'transparent',
    },
  },
  // Styles for the contract template column
  contractTemplateCell: {
    // Hide the icon by default; it will be displayed on hover
    '& [data-attrib=hovered-icon]': {
      opacity: 0,
      transform: 'translateX(-100%)',
      transition: '.2s ease',
      width: 0,
    },
    '&:hover': {
      // Show the icon and move it back to its original position on hover
      '& [data-attrib=hovered-icon]': {
        opacity: 1,
        transform: 'translateX(0px)',
        width: 'auto',
      },
      // Hide the default icon during hover
      '& [data-attrib=default-icon]': {
        display: 'none',
      },
    },
  },
  infoIcon: {
    height: 16,
  },
  tooltip: {
    background: white,
    color: black,
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    ...typography13RegularStyle,
  },
}));

export const isContractTemplateDeleted = (
  templates: ContractTemplate[],
  templateId: string,
) => !templates.find((c) => c.id === templateId);

const CellRendererFramework = ({ valueFormatted }: ICellRendererParams) => {
  return <MemoBaseTypography>{valueFormatted}</MemoBaseTypography>;
};

interface ContractTemplateCellRendererProps extends ICellRendererParams {
  data: Contract;
  templateIds: Set<string>;
  areTemplatesLoaded: boolean;
}

const ContractTemplateCellRenderer = ({
  data,
  templateIds,
  areTemplatesLoaded,
}: ContractTemplateCellRendererProps) => {
  const { classes } = useStyles();
  const isTemplateDeleted =
    areTemplatesLoaded && !templateIds.has(data.contractTemplateId);

  return (
    <Button
      className={classes.button}
      endIcon={
        isTemplateDeleted ? (
          <Tooltip
            classes={{
              tooltip: classes.tooltip,
            }}
            title={
              !data.contractTemplateId
                ? 'This is a one-off contract'
                : 'This contract template was deleted.'
            }
            placement="bottom-start"
          >
            <div>
              <InformationCircleIcon className={classes.infoIcon} />
            </div>
          </Tooltip>
        ) : (
          <>
            <ArrowRightIcon data-attrib="hovered-icon" />
            <ForwardIcon data-attrib="default-icon" />
          </>
        )
      }
    >
      <MemoBaseTypography>{data.name}</MemoBaseTypography>
    </Button>
  );
};

const frameworkComponents = {
  templateActionRenderer: SubmissionTableActionMenu,
  contractTemplateCellRenderer: ContractTemplateCellRenderer,
  statusCellRenderer: StatusCellRenderer,
  contractClientRenderer: ContractClientRenderer,
};

type Props = {
  client?: Client;
  onClickNewContract?: () => void;
  onContractRowClick?: (contract: Contract) => void;
};

export const SubmissionListTable = ({
  client,
  onClickNewContract,
  onContractRowClick,
}: Props) => {
  const { EnableContextualActions } = useFeatureFlags();
  const { t } = useTranslation();
  const onClientDetail = !!client;
  const { classes } = useStyles();
  const clientList = useAppSelector((state) => state.clients.clients);
  const clients = useMemo(() => arrayToMap(clientList, 'id'), [clientList]);
  const companies = useAppSelector(selectAllCompaniesMap);
  const portalConfig = React.useContext(PortalConfigContext);
  const { data: contracts, isLoading: areContractsLoading } =
    useGetContractsQuery(onClientDetail ? { recipientId: client?.id } : {});
  const { data: templates, isSuccess: areTemplatesLoaded } =
    useGetContractTemplatesQuery();
  const contractTemplateIds = useMemo(
    () => new Set((templates ?? []).map((template) => template.id)),
    [templates],
  );
  const searchKey = useAppSelector((state) => state.ui.searchValue);
  const Icon = useModuleIcon('contracts');

  const columns: ColDef[] = useMemo(
    () => [
      // client detail page is already showing contracts assigned to a particular client
      // so as all the recipient column would be showing same client, so doesn't make sense to show the column
      ...(onClientDetail
        ? []
        : [
            {
              headerName: 'Recipient',
              field: 'recipient',
              minWidth: 350,
              sortable: true,
              comparator: (
                valueA: { givenName: string; familyName: string },
                valueB: { givenName: string; familyName: string },
              ) => {
                const firstFullName = `${valueA.givenName} ${valueA.familyName}`;
                const secondFullName = `${valueB.givenName} ${valueB.familyName}`;
                return firstFullName.localeCompare(secondFullName);
              },
              getQuickFilterText(
                filterParams: GetQuickFilterTextParams,
              ): string {
                return [
                  filterParams.data.recipientData.client?.givenName,
                  filterParams.data.recipientData.client?.familyName,
                  filterParams.data.recipientData.client?.email,
                  filterParams.data.recipientData.company?.name,
                ]
                  .filter(Boolean)
                  .join(' ');
              },
              cellRenderer: 'contractClientRenderer',
            },
          ]),
      {
        headerName: 'Contract template',
        field: 'name',
        cellRenderer: 'contractTemplateCellRenderer',
        cellRendererParams: {
          templateIds: contractTemplateIds,
          areTemplatesLoaded,
        },
        minWidth: 400,
        sortable: true,
        cellClass: classes.contractTemplateCell,
        // redirect to contract details page when cell is clicked
        // Skip navigation when onContractRowClick is provided (immersive modal handles the row click)
        onCellClicked: onContractRowClick
          ? undefined
          : (e) => {
              if (
                areTemplatesLoaded &&
                !contractTemplateIds.has(e.data.contractTemplateId)
              )
                return;

              history.push(
                `${CONTRACT_DETAILS_PAGE.path}?templateId=${e.data.contractTemplateId}&view=${ContractDetailsPageView.SubmissionRequests}`,
              );
            },
      },
      {
        headerName: 'Sent on',
        field: 'shareDate',
        minWidth: 200,
        cellRenderer: CellRendererFramework,
        getQuickFilterText(filterParams) {
          return DateUtils.formatDate(filterParams.data.sentOn);
        },
        valueFormatter: ({ value }) =>
          value ? DateUtils.formatDate(value) : '',
        sortable: true,
        sort: 'desc',
      },
      {
        headerName: 'Submitted on',
        field: 'submissionDate',
        minWidth: 200,
        cellRenderer: CellRendererFramework,
        getQuickFilterText(filterParams) {
          return filterParams.data.sentOn
            ? DateUtils.formatDate(filterParams.data.sentOn)
            : '';
        },
        valueFormatter: ({ value }) =>
          value ? DateUtils.formatDate(value) : '',
      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 150,
        sortable: true,
        cellRenderer: 'statusCellRenderer',
        cellRendererParams: {
          getStatusVariant: (status: ContractStatus) => {
            return getContractStatusInfo(status).statusVariant;
          },
        },
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        suppressSizeToFit: true,
        pinned: 'right',
        cellRenderer: 'templateActionRenderer',
        cellRendererParams: {
          clientsMap: clients,
          enableMagicLinks: portalConfig.authPreferences.enableMagicLinks,
        },
      },
    ],
    [
      onClientDetail,
      classes.contractTemplateCell,
      onContractRowClick,
      contractTemplateIds,
      areTemplatesLoaded,
      clients,
      portalConfig.authPreferences.enableMagicLinks,
    ],
  );

  const rowData = useMemo(() => {
    if (!contracts) return [];

    const contractsWithClientInfo = contracts.map((item) => {
      const companyFields = companies.get(item.companyId)?.fields || {};
      const clientFields = clients.get(item.clientId)?.fields || {};
      return {
        ...item,
        recipientData: {
          client: clientFields,
          company: companyFields,
        },
      };
    });

    return contractsWithClientInfo;
  }, [contracts, clients, companies]);

  const shouldShowStandardPage = !areContractsLoading && !rowData.length;

  const handleContractRowClick = React.useCallback(
    (value: Contract) => {
      if (onContractRowClick) {
        onContractRowClick(value);
      } else {
        history.push(
          `${CONTRACTS_PAGE.path}/submission?contractId=${value.id}`,
        );
      }
    },
    [onContractRowClick],
  );

  const handleStandardPagePrimaryAction = () => {
    if (!onClientDetail && onClickNewContract) {
      onClickNewContract();
      return;
    }
    const targetUri = onClientDetail
      ? `${CONTRACTS_PAGE.path}`
      : `${CONTRACTS_PAGE.path}/edit`;
    history.push(targetUri);
  };

  const standardPageParams = {
    title: EnableContextualActions
      ? 'No contracts to show'
      : 'No contracts submitted or assigned',
    topIcon: Icon,
    desc:
      onClientDetail && client
        ? EnableContextualActions
          ? `Send this ${t(($) => $.client, { count: 1 }).toLowerCase()} their first contract`
          : t(($) => $.page_contracts.no_contracts_for_client_description, {
              givenName: client.fields.givenName,
              familyName: client.fields.familyName,
            })
        : t(($) => $.page_contracts.no_contracts_sent_description),
    actionButton: onClientDetail ? (
      !EnableContextualActions ? (
        <Button
          color="primary"
          id="btn-create-first-invoice"
          onClick={handleStandardPagePrimaryAction}
          endIcon={<ArrowRightIcon />}
          variant="contained"
        >
          {'Go to contracts'}
        </Button>
      ) : null
    ) : (
      <CreateButton
        htmlId="btn-create-first-invoice"
        onClick={handleStandardPagePrimaryAction}
      >
        {'New contract'}
      </CreateButton>
    ),
  };

  return (
    <LoadingWrapper fullHeight isLoading={areContractsLoading}>
      {shouldShowStandardPage ? (
        <StandardPage {...standardPageParams} />
      ) : (
        <TableWrapper
          height={`calc(100% - ${TABS_CONTAINER_HEIGHT + 12}px)`} // 12px is to adjust the top spacing
          className={classNames('w-full', {
            [classes.contractsRoot]: !onClientDetail,
          })}
        >
          <AgGrid
            className="agGridRowHover"
            rowData={rowData}
            columnDefs={columns}
            searchKey={onClientDetail ? undefined : searchKey}
            onRowClicked={handleContractRowClick}
            frameworkComponents={frameworkComponents}
          />
        </TableWrapper>
      )}
    </LoadingWrapper>
  );
};
